import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/ui/table"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/ui/dropdown-menu"

import { Button } from "@/ui/button"
import { MaterialIcon } from "@/ui/material-icon"
import styles from "./DataTable.module.scss"

type Column<T> = {
    key: keyof T | string
    header: string
    render?: (row: T) => React.ReactNode
}

type Action<T> = {
    icon?: string
    label: string
    onClick: (row: T) => void
}

type DataTableProps<T> = {
    columns: Column<T>[]
    data: T[]
    actions?: Action<T>[]
}

export function DataTable<T>({
    columns,
    data,
    actions,
}: DataTableProps<T>) {
    return (
        <div className={styles.container}>
            <Table>
                {/* HEADER */}
                <TableHeader className={styles.headerBg}>
                    <TableRow>
                        {columns.map((col) => (
                            <TableHead key={col.key as string} className={styles.headerCell}>
                                {col.header}
                            </TableHead>
                        ))}
                        {actions && <TableHead />}
                    </TableRow>
                </TableHeader>

                {/* BODY */}
                <TableBody>
                    {data.map((row, i) => (
                        <TableRow
                            key={i}
                            className={i % 2 === 0 ? styles.oddRow : styles.evenRow}
                        >
                            {columns.map((col) => {
                                const rawValue = col.render
                                    ? col.render(row)
                                    : (row as any)[col.key]

                                const cellValue =
                                    rawValue === null ||
                                        rawValue === undefined ||
                                        rawValue === ""
                                        ? "-"
                                        : rawValue

                                return (
                                    <TableCell key={col.key as string} className={styles.cell}>
                                        {cellValue}
                                    </TableCell>
                                )
                            })}

                            {/* ACTIONS */}
                            {actions && (
                                <TableCell className={styles.cellRight}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon">
                                                <MaterialIcon name="more_vert" size={16} />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            {actions.map((action, idx) => (
                                                <DropdownMenuItem
                                                    key={idx}
                                                    onClick={() => action.onClick(row)}
                                                >
                                                    {action.icon && (
                                                        <MaterialIcon
                                                            name={action.icon}
                                                            size={16}
                                                        />
                                                    )}
                                                    {action.label}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}

                    {data.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length + (actions ? 1 : 0)}
                                className={styles.emptyRow}
                            >
                                No Data is present
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}